// ============================================================================
// PAYMENT HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { paymentService } from 'api/services/paymentService';
import type {
  Payment,
  CreatePaymentRequest,
  UpdatePaymentRequest,
  PaymentSearchRequest,
  ID,
} from 'api/types';

/** Standalone Advance Payments grid screen. */
export const usePayments = (params?: PaymentSearchRequest) => {
  const [data, setData] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await paymentService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { data, loading, error, pagination, refetch: fetchAll };
};

/** Booking-level payments tab — returns all payment rows for a single booking. */
export const useBookingPayments = (bookingId: ID, includeVoided = false) => {
  const [data, setData] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAll = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await paymentService.getByBookingId(bookingId, includeVoided);
      setData(response.data ?? []);
    } catch (err) { setError(err as Error); }
    finally { setLoading(false); }
  }, [bookingId, includeVoided]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return { data, loading, error, refetch: fetchAll };
};

export const useCreatePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createPayment = async (
    data: CreatePaymentRequest,
    options?: { onSuccess?: (item: Payment) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await paymentService.create(data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally { setLoading(false); }
  };

  return { createPayment, loading, error };
};

export const useUpdatePayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updatePayment = async (
    id: ID,
    data: UpdatePaymentRequest,
    options?: { onSuccess?: (item: Payment) => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      const response = await paymentService.update(id, data);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally { setLoading(false); }
  };

  return { updatePayment, loading, error };
};

export const useVoidPayment = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const voidPayment = async (
    id: ID,
    rowVersion: number,
    options?: { onSuccess?: () => void; onError?: (error: Error) => void }
  ) => {
    setLoading(true); setError(null);
    try {
      await paymentService.void(id, rowVersion);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally { setLoading(false); }
  };

  return { voidPayment, loading, error };
};
